import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  X,
  Keyboard,
  Zap,
  Send,
  Delete,
  Trash2,
  Volume2,
  Type,
  Smile,
  ArrowRight,
  ArrowLeft,
  Check,
  Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { speak } from "@/lib/ttsService";
import {
  getPredictionEngine,
  WordEntry,
  PhraseEntry,
} from "@/lib/aacPredictionEngine";
import { MainLayout } from "@/components/layout/MainLayout";
import { useToast } from "@/hooks/use-toast";

type InputMethodType = "keyboard" | "quick" | "emoji";
type KeyboardTab = "letters" | "numbers" | "symbols";
type AutocompleteMode = "standard" | "acronym";
type ComposerStep = "recipient" | "message";

// Keyboard layouts
const hebrewLayout = [
  ["פ", "ם", "ן", "ו", "ט", "א", "ר", "ק"],
  ["ף", "ך", "ל", "ח", "י", "ע", "כ", "ג", "ד"],
  ["ץ", "ת", "צ", "מ", "נ", "ה", "ב", "ס", "ז", "ש"],
];

const englishLayout = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M"],
];

const numberLayout = [
  ["1", "2", "3", "4", "5"],
  ["6", "7", "8", "9", "0"],
  [".", ",", "-", "+", "="],
];

const symbolLayout = [
  ["!", "@", "#", "$", "%"],
  ["&", "*", "(", ")", "/"],
  ["?", ":", ";", '"', "'"],
];

// Quick phrases for contact
const quickPhrases = {
  he: [
    "אני בסדר",
    "בוא אליי",
    "צריך עזרה",
    "תתקשר אליי",
    "אני אוהב אותך",
    "תודה",
    "מתי תבוא?",
    "אני מחכה",
    "הכל טוב",
    "אני צריך משהו",
    "אני עייף",
    "אני רעב",
  ],
  en: [
    "I'm okay",
    "Come to me",
    "Need help",
    "Call me",
    "I love you",
    "Thank you",
    "When are you coming?",
    "I'm waiting",
    "Everything is fine",
    "I need something",
    "I'm tired",
    "I'm hungry",
  ],
};

// Emoji - all in one big array
const allEmojis = [
  "😊", "😀", "😃", "😄", "😁", "😅", "😂", "🤣", "😇", "😉", "😍", "🥰", "😘", "😋", "😎",
  "🥺", "😢", "😭", "😤", "😠", "🤔", "🤗", "🤭", "🤫", "🤐", "😴", "🥱", "😷", "🤒", "🤕",
  "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💕", "💞", "💓", "💗", "💖", "💝", "💘",
  "👍", "👎", "👌", "✌️", "🤞", "🤟", "🤘", "🤙", "👋", "🤚", "✋", "🖐️", "👏", "🙌", "🙏",
];

interface Suggestion {
  text: string;
  type: "word" | "phrase";
  acronym?: string;
}

export default function ContactsCompose() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, dir, isRTL } = useTranslation();
  const { toast } = useToast();
  
  // Get data from location state or query params
  const params = new URLSearchParams(location.search);
  const type = (params.get("type") as "email" | "push") || "email";
  const recipientInfo = params.get("recipient") || location.state?.recipientInfo || "";
  const returnPath = params.get("return") || location.state?.returnPath || "/contacts";
  
  // For push notifications, skip recipient step entirely
  // For email, skip recipient step if email is already provided
  const skipRecipientStep = type === "push" || !!recipientInfo;
  const [step, setStep] = useState<ComposerStep>(skipRecipientStep ? "message" : "recipient");
  // Default to quick phrases for both email and push
  const [inputMethodView, setInputMethodView] = useState<InputMethodType>("quick");
  const [message, setMessage] = useState("");
  const [recipient, setRecipient] = useState(recipientInfo);
  
  const [language, setLanguage] = useState<"hebrew" | "english">(
    step === "recipient" ? "english" : (lang === "he" ? "hebrew" : "english")
  );
  const [tab, setTab] = useState<KeyboardTab>("letters");
  const [mode, setMode] = useState<AutocompleteMode>("standard");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const previousWordRef = useRef<string | null>(null);
  const engineRef = useRef(getPredictionEngine());

  const phrases = lang === "en" ? quickPhrases.en : quickPhrases.he;
  const isUIHebrew = lang === "he";

  useEffect(() => {
    if (step === "recipient") {
      setLanguage("english");
    }
  }, [step]);

  const getActiveLayout = () => {
    if (tab === "letters") return language === "hebrew" ? hebrewLayout : englishLayout;
    if (tab === "numbers") return numberLayout;
    return symbolLayout;
  };

  const activeLayout = getActiveLayout();
  const currentValue = step === "recipient" ? recipient : message;
  const setCurrentValue = step === "recipient" ? setRecipient : setMessage;

  useEffect(() => {
    if (step !== "message") {
      setSuggestions([]);
      return;
    }

    const engine = engineRef.current;
    
    if (!message.trim() && mode === "acronym") {
      setSuggestions([]);
      return;
    }

    if (mode === "acronym") {
      const currentWord = message.trim().split(/\s+/).pop() || "";
      const results = engine.searchAcronym(currentWord);
      setSuggestions(
        results.map((r) => ({
          text: r.phrase,
          type: "phrase" as const,
          acronym: r.acronym,
        }))
      );
    } else {
      const results = engine.searchStandard(message);
      setSuggestions(
        results.map((r) => ({
          text: r.word,
          type: "word" as const,
        }))
      );
    }
  }, [message, mode, step]);

  const getTitle = () => {
    if (step === "recipient") {
      return isUIHebrew ? "הזנת כתובת מייל" : "Enter Email Address";
    }
    if (type === "push") {
      return isUIHebrew ? "שליחת התראת Push" : "Send Push Notification";
    }
    return isUIHebrew ? "כתיבת הודעה" : "Compose Message";
  };

  const handleQuickPhrase = (phrase: string) => {
    setMessage((prev) => (prev ? prev + " " + phrase : phrase));
  };

  const handleEmoji = (emoji: string) => {
    setCurrentValue((prev) => prev + emoji);
  };

  const handleKeyPress = (key: string) => {
    setCurrentValue((prev) => prev + key);
  };

  const handleBackspace = () => {
    setCurrentValue((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    setCurrentValue("");
  };

  const handleSpace = () => {
    setCurrentValue((prev) => prev + " ");
  };

  const handleSpeak = async () => {
    if (!message.trim()) return;
    await speak({ text: message });
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    
    if (type === "push") {
      // Send push notification directly
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: userData } = await supabase.auth.getUser();
        
        if (!userData.user?.id) {
          toast({
            title: t("error"),
            description: t("pleaseLoginFirst"),
            variant: "destructive",
          });
          return;
        }
        
        const { data, error } = await supabase.functions.invoke('send-push-alert', {
          body: {
            patientId: userData.user.id,
            alertType: 'help',
            message: message.trim()
          }
        });
        
        if (error) throw error;
        
        if (data?.sent > 0) {
          toast({
            title: t("notificationSent"),
            description: t("sentToDevices").replace("{count}", String(data.sent)),
          });
          navigate(returnPath);
        } else {
          toast({
            title: t("noCaregiversConnected"),
            description: t("scanQRInSettings"),
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: t("error"),
          description: t("failedToSendNotification"),
          variant: "destructive",
        });
      }
    } else {
      // Email - store message and navigate back
      sessionStorage.setItem("composedMessage", message);
      sessionStorage.setItem("composedRecipient", recipient);
      sessionStorage.setItem("composeAction", "send");
      
      navigate(returnPath, { 
        state: { 
          composedMessage: message, 
          composedRecipient: recipient,
          action: "send"
        } 
      });
    }
  };

  const handleConfirmRecipient = () => {
    if (!recipient.trim()) return;
    setStep("message");
    setLanguage(lang === "he" ? "hebrew" : "english");
  };

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    const words = message.trim().split(/\s+/);
    words.pop();
    const newMessage = [...words, suggestion.text].join(" ") + " ";
    setMessage(newMessage);
  };

  const handleBack = () => {
    navigate(returnPath);
  };

  return (
    <MainLayout onBack={handleBack}>
      <div className="flex-1 flex flex-col overflow-hidden" dir={dir}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-card">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack} data-scan-item>
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-foreground">{getTitle()}</h1>
          </div>
          
          {step === "message" && (
            <div className="flex items-center gap-2">
              <Button
                variant={inputMethodView === "keyboard" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setInputMethodView("keyboard")}
                data-scan-item
                className="w-12 h-12"
              >
                <Keyboard className="w-6 h-6" />
              </Button>
              <Button
                variant={inputMethodView === "quick" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setInputMethodView("quick")}
                data-scan-item
                className="w-12 h-12"
              >
                <Zap className="w-6 h-6" />
              </Button>
              <Button
                variant={inputMethodView === "emoji" ? "secondary" : "ghost"}
                size="icon"
                onClick={() => setInputMethodView("emoji")}
                data-scan-item
                className="w-12 h-12"
              >
                <Smile className="w-6 h-6" />
              </Button>
            </div>
          )}
        </div>

        {/* Input display */}
        <div className="p-4 bg-muted/50">
          <div className="min-h-[80px] p-4 rounded-xl bg-background border-2 border-border text-xl">
            {currentValue || (
              <span className="text-muted-foreground">
                {step === "recipient"
                  ? (type === "email" ? "example@email.com" : "050-000-0000")
                  : (isUIHebrew ? "הקלד הודעה..." : "Type message...")}
              </span>
            )}
          </div>
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-card">
          {step === "message" && (
            <Button variant="outline" size="lg" onClick={handleSpeak} data-scan-item className="gap-2">
              <Volume2 className="w-5 h-5" />
            </Button>
          )}
          <Button variant="outline" size="lg" onClick={handleBackspace} data-scan-item className="gap-2">
            <Delete className="w-5 h-5" />
          </Button>
          <Button variant="outline" size="lg" onClick={handleClear} data-scan-item className="gap-2">
            <Trash2 className="w-5 h-5" />
          </Button>
          <div className="flex-1" />
          {step === "recipient" ? (
            <Button size="lg" onClick={handleConfirmRecipient} disabled={!recipient.trim()} data-scan-item className="gap-2 px-8">
              <Check className="w-5 h-5" />
              {isUIHebrew ? "המשך" : "Continue"}
            </Button>
          ) : (
            <Button size="lg" onClick={handleSend} disabled={!message.trim()} data-scan-item className="gap-2 px-8">
              <Send className="w-5 h-5" />
              {isUIHebrew ? "שלח" : "Send"}
            </Button>
          )}
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-auto p-4">
          {step === "recipient" ? (
            // Recipient keyboard
            <div className="space-y-4">
              {type === "email" && (
                <>
                  {/* Special chars for email */}
                  <div className="flex flex-wrap gap-2 justify-center">
                    {["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "-", "_", ".", "@"].map((char) => (
                      <Button
                        key={char}
                        variant="outline"
                        onClick={() => handleKeyPress(char)}
                        data-scan-item
                        className="w-12 h-12 text-lg font-mono"
                      >
                        {char}
                      </Button>
                    ))}
                  </div>
                  
                  {/* Letter keyboard */}
                  {englishLayout.map((row, ri) => (
                    <div key={ri} className="flex gap-2 justify-center">
                      {row.map((key) => (
                        <Button
                          key={key}
                          variant="outline"
                          onClick={() => handleKeyPress(key.toLowerCase())}
                          data-scan-item
                          className="w-12 h-12 text-lg"
                        >
                          {key}
                        </Button>
                      ))}
                    </div>
                  ))}
                  
                  {/* Domain shortcuts */}
                  <div className="flex gap-2 justify-center">
                    <Button variant="secondary" onClick={() => handleKeyPress(".com")} data-scan-item className="h-12 px-6">
                      .com
                    </Button>
                    <Button variant="secondary" onClick={() => handleKeyPress(".co.il")} data-scan-item className="h-12 px-6">
                      .co.il
                    </Button>
                    <Button variant="secondary" onClick={() => handleKeyPress("@gmail.com")} data-scan-item className="h-12 px-6">
                      @gmail.com
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : inputMethodView === "keyboard" ? (
            // Message keyboard
            <div className="space-y-4">
              {/* Mode and language toggles */}
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button
                    variant={mode === "standard" ? "secondary" : "outline"}
                    onClick={() => setMode("standard")}
                    size="lg"
                    data-scan-item
                    className="gap-2"
                  >
                    <Type className="w-5 h-5" />
                    {isUIHebrew ? "רגיל" : "Standard"}
                  </Button>
                  <Button
                    variant={mode === "acronym" ? "secondary" : "outline"}
                    onClick={() => setMode("acronym")}
                    size="lg"
                    data-scan-item
                    className="gap-2"
                  >
                    <Zap className="w-5 h-5" />
                    {isUIHebrew ? 'ר"ת' : "Acronym"}
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant={tab === "letters" ? "secondary" : "outline"}
                    onClick={() => setTab("letters")}
                    size="lg"
                    data-scan-item
                  >
                    ABC
                  </Button>
                  <Button
                    variant={tab === "numbers" ? "secondary" : "outline"}
                    onClick={() => setTab("numbers")}
                    size="lg"
                    data-scan-item
                  >
                    123
                  </Button>
                  <Button
                    variant={tab === "symbols" ? "secondary" : "outline"}
                    onClick={() => setTab("symbols")}
                    size="lg"
                    data-scan-item
                  >
                    #@!
                  </Button>
                </div>
                
                {tab === "letters" && (
                  <div className="flex gap-2">
                    <Button
                      variant={language === "hebrew" ? "secondary" : "outline"}
                      onClick={() => setLanguage("hebrew")}
                      size="lg"
                      data-scan-item
                    >
                      עב
                    </Button>
                    <Button
                      variant={language === "english" ? "secondary" : "outline"}
                      onClick={() => setLanguage("english")}
                      size="lg"
                      data-scan-item
                    >
                      EN
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {suggestions.slice(0, 5).map((s, i) => (
                    <Button
                      key={i}
                      variant="secondary"
                      onClick={() => handleSelectSuggestion(s)}
                      data-scan-item
                      className="h-12"
                    >
                      {s.text}
                      {s.acronym && <span className="text-xs text-muted-foreground mr-2">({s.acronym})</span>}
                    </Button>
                  ))}
                </div>
              )}
              
              {/* Keyboard */}
              {activeLayout.map((row, ri) => (
                <div key={ri} className="flex gap-2 justify-center">
                  {row.map((key) => (
                    <Button
                      key={key}
                      variant="outline"
                      onClick={() => handleKeyPress(key)}
                      data-scan-item
                      className="w-12 h-14 text-xl"
                    >
                      {key}
                    </Button>
                  ))}
                </div>
              ))}
              
              {/* Bottom row */}
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={handleBackspace} data-scan-item className="h-14 px-6">
                  <Delete className="w-6 h-6" />
                </Button>
                {[".", ",", "?", "!"].map((p) => (
                  <Button key={p} variant="outline" onClick={() => handleKeyPress(p)} data-scan-item className="w-12 h-14 text-xl">
                    {p}
                  </Button>
                ))}
                <Button variant="secondary" onClick={handleSpace} data-scan-item className="flex-1 h-14 text-lg">
                  {isUIHebrew ? "רווח" : "Space"}
                </Button>
              </div>
            </div>
          ) : inputMethodView === "quick" ? (
            // Quick phrases
            <div className="grid grid-cols-3 gap-3">
              {phrases.map((phrase, i) => (
                <Button
                  key={i}
                  variant="outline"
                  onClick={() => handleQuickPhrase(phrase)}
                  data-scan-item
                  className="h-20 text-lg whitespace-normal text-center"
                >
                  {phrase}
                </Button>
              ))}
            </div>
          ) : (
            // Emoji grid
            <div className="grid grid-cols-8 gap-2">
              {allEmojis.map((emoji, i) => (
                <Button
                  key={i}
                  variant="ghost"
                  onClick={() => handleEmoji(emoji)}
                  data-scan-item
                  className="w-14 h-14 text-3xl p-0"
                >
                  {emoji}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
